import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, ChevronLeft, ChevronRight, Building2, CheckCircle2, FileText } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { exportRegistroIngressi } from "../lib/exporter";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const meseLabel = (ym) => { const [y, m] = ym.split("-"); return `${MESI[Number(m) - 1]} ${y}`; };

// elenco mesi da (anno corrente -1) a (anno corrente +1)
const buildMonths = () => {
  const now = new Date();
  const out = [];
  for (let y = now.getFullYear() - 1; y <= now.getFullYear() + 1; y++)
    for (let m = 1; m <= 12; m++) out.push(`${y}-${String(m).padStart(2, "0")}`);
  return out;
};

const presente = (r, ym) => {
  const ps = (r.periodi && r.periodi.length) ? r.periodi : [{ ingresso: r.data_ingresso, dimissione: r.data_dimissione }];
  return ps.some((p) => { const i = (p.ingresso || "").slice(0, 7); const o = (p.dimissione || "").slice(0, 7); return (!i || i <= ym) && (!o || o >= ym); });
};

export default function IngressiRegistro() {
  const { structures = [], companyLogo } = useAuth();
  const months = useMemo(buildMonths, []);
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));

  const { data: ospiti = [], isLoading } = useQuery({
    queryKey: ["registro-ospiti"],
    queryFn: async () => (await api.get("/ospiti")).data,
  });

  const shiftMese = (dir) => {
    const idx = months.indexOf(mese);
    const next = months[idx + dir];
    if (next) setMese(next);
  };

  // registrati per struttura nel mese selezionato
  const perStruct = useMemo(() => {
    return structures.map((s) => {
      const registrati = ospiti
        .filter((o) => o.structure_id === s.id && presente(o, mese) && !!(o.registro_mesi && o.registro_mesi[mese]))
        .sort((a, b) => (a.cognome || "").localeCompare(b.cognome || "", "it") || (a.nome || "").localeCompare(b.nome || "", "it"));
      return { struct: s, registrati, posti: Number(s.posti_accreditati || 0) };
    });
  }, [structures, ospiti, mese]);

  const totReg = perStruct.reduce((sum, p) => sum + p.registrati.length, 0);
  const totPosti = perStruct.reduce((sum, p) => sum + p.posti, 0);

  const scaricaPdf = () => exportRegistroIngressi({
    meseLabel: meseLabel(mese),
    perStruct: perStruct.map(({ struct, registrati, posti }) => ({ nome: struct.nome, posti, registrati: registrati.map((o) => ({ cognome: o.cognome, nome: o.nome, camera: o.camera })) })),
    logo: companyLogo,
  });

  return (
    <div className="p-4 sm:p-6 lg:px-10 space-y-6" data-testid="ingressi-registro-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-sky-100 grid place-items-center shrink-0"><ClipboardList className="text-sky-600" size={22} /></div>
        <div>
          <h1 className="text-2xl font-head font-bold">Registro</h1>
          <p className="text-sm text-muted-foreground">Ospiti ufficialmente registrati per struttura (dalla colonna "Reg"). Il conteggio dovrebbe coincidere con i posti accreditati.</p>
        </div>
      </div>

      {/* Navigatore mese */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="icon" onClick={() => shiftMese(-1)} data-testid="reg-month-prev"><ChevronLeft size={18} /></Button>
        <Select value={mese} onValueChange={setMese}>
          <SelectTrigger className="w-[200px] font-semibold" data-testid="reg-month-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {months.map((m) => <SelectItem key={m} value={m} data-testid={`reg-month-opt-${m}`}>{meseLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={() => shiftMese(1)} data-testid="reg-month-next"><ChevronRight size={18} /></Button>
        <Button variant="outline" onClick={scaricaPdf} className="font-semibold" data-testid="reg-export-pdf"><FileText size={15} className="mr-1.5" /> Scarica PDF</Button>

        <div className="ml-auto flex items-center gap-2 rounded-xl border bg-white px-4 py-2" data-testid="reg-totale">
          <span className="text-xs font-semibold uppercase text-muted-foreground">Totale registrati</span>
          <span className={`text-2xl font-head font-bold ${totReg === totPosti && totPosti > 0 ? "text-emerald-600" : "text-sky-700"}`}>{totReg}</span>
          <span className="text-sm text-muted-foreground">/ {totPosti} posti accreditati</span>
          {totReg === totPosti && totPosti > 0 && <CheckCircle2 size={18} className="text-emerald-500" />}
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Caricamento…</p>
      ) : perStruct.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">Nessuna struttura disponibile.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {perStruct.map(({ struct, registrati, posti }) => {
            const match = registrati.length === posti && posti > 0;
            const over = registrati.length > posti;
            return (
              <div key={struct.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`reg-struct-${struct.id}`}>
                <div className="px-4 py-3 bg-slate-50 border-b flex items-center gap-2">
                  <Building2 size={18} className="text-sky-600 shrink-0" />
                  <span className="font-bold flex-1 truncate">{struct.nome}</span>
                  <span className={`text-sm font-bold rounded-full px-3 py-1 ${match ? "bg-emerald-100 text-emerald-700" : over ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`reg-count-${struct.id}`}>
                    {registrati.length} / {posti} posti
                  </span>
                </div>
                <div className="p-3">
                  {registrati.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4 text-center">Nessun registrato in questo mese.</div>
                  ) : (
                    <ol className="space-y-1">
                      {registrati.map((o, i) => (
                        <li key={o.id} className="flex items-center gap-2.5 text-sm py-1 border-b last:border-0" data-testid={`reg-name-${o.id}`}>
                          <span className="w-6 h-6 rounded-full bg-sky-50 text-sky-700 grid place-items-center text-xs font-bold tabnum shrink-0">{i + 1}</span>
                          <span className="font-semibold flex-1 min-w-0 truncate">{[o.cognome, o.nome].filter(Boolean).join(" ") || "—"}</span>
                          {o.camera && <span className="text-[11px] text-sky-600 font-semibold shrink-0">Camera/zona {o.camera}</span>}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
